import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Motion from "../components/Motion";
import { SpeedInsights } from "@vercel/speed-insights/react";

// Asset Imports
import appKairos from "../assets/appKairos.svg";
import datacyImg from "../assets/datacy.png";
import portAmdocs from "../assets/port_amdocs.png";
import portAsean from "../assets/port_asean.png";
import portCustomerTransaction from "../assets/port_customer_transaction.png";
import portDatacoSupplychain from "../assets/port_dataco_supplychain.png";
import portRakso from "../assets/port_rakso.png";
import portSas from "../assets/port_sas.png";
import portShoplifting from "../assets/port_shoplifting.png";
import portTechvisionB2b from "../assets/port_techvision_b2b.png";
import portTiktok from "../assets/port_tiktok.png";

// Custom hook for scroll reveal animations
function useScrollReveal() {
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal--visible");
          } else {
            entry.target.classList.remove("reveal--visible");
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    const elements = document.querySelectorAll(".reveal-on-scroll, .reveal-initial");
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);
}

// Filter Categories
const CATEGORIES = [
  "All",
  "Data Science",
  "Data Analysis",
  "Software Development",
  "Project Management",
];

// Complete Projects Dataset (Updated directly from resume)
const projectsData = [
  {
    id: "b2b-wholesale",
    role: "TechVision B2B Electronics Data Pipeline",
    org: "B2B Wholesale Analytics, India",
    link: "https://dbc-c672d104-4132.cloud.databricks.com/dashboardsv3/01f1b79f7970100398e3600201e19a00/published/pages/product_performance?o=7474647654101349",
    date: "Sept 2026",
    image: portTechvisionB2b,
    categories: ["Data Science", "Data Analysis"],
    points: [
      "Engineered an automated Databricks IDP pipeline to process 100 invoice PDFs, eliminating manual extraction overhead while achieving a 100% data quality validation pass rate.",
      "Segmented B2B buyers using K-Means (RFM) and CLV modeling, identifying key accounts driving 59% of revenue (₹76M+) and flagging at-risk accounts for targeted churn prevention.",
      "Applied Isolation Forest anomaly detection to automatically flag pricing and extraction errors across 5.1% of line items, establishing a production audit queue to prevent financial reporting discrepancies.",
      "Mined cross-sell patterns using Spark MLlib FPGrowth (up to 4.17 lift) to unlock ₹20L+ in incremental bundle revenue; built a 5-page Databricks AI/BI dashboard and Prophet model forecasting ₹128M–₹149M Q3 demand."
    ],
  },
  {
    id: "ecommerce-behavioral",
    role: "Customer 360 Transactional Data Analysis & Dashboard",
    org: "E-Commerce Behavioral, Indonesia",
    link: "https://dbc-c672d104-4132.cloud.databricks.com/dashboardsv3/01f1b2aba61a1687a10b037bd3232616/published?o=7474647654101349",
    date: "Sept 2026",
    image: portCustomerTransaction,
    categories: ["Data Analysis", "Data Science"],
    points: [
      "Built a Databricks ETL pipeline consolidating 8M+ click-stream logs and transactional tables into a unified analytical dataset covering 50K users.",
      "Executed SQL-based RFM and behavioral segmentation, discovering that the top 36.4% \"Champions\" segment drives 85% of total revenue (1.56T IDR) while identifying 3.3K at-risk customers representing 111B IDR in recoverable value.",
      "Modeled multi-stage event funnels in Python to isolate a 23.4% homepage drop-off and evaluate promotional ROI (180x–187x returns across top campaigns).",
      "Built interactive Tableau dashboards tracking category cross-sell (>90% multi-category penetration) and mobile engagement (90% session share), delivering data-backed recommendations for conversion optimization."
    ],
  },
  {
    id: "dengue-identification",
    role: "Regional Dengue Case Features Identification",
    org: "ASEAN Data Science Explorer 2026",
    link: "https://canva.link/dr7r5uzo3t7snla",
    date: "Sept 2026",
    image: portAsean,
    categories: ["Data Science", "Data Analysis", "Project Management"],
    points: [
      "Engineered an end-to-end ML pipeline predicting regional dengue incidence across 17 Philippine regions, merging 5 disparate datasets (GRDP, population, land area, NASA POWER climate data, case counts) spanning 2000–2025.",
      "Resolved a population-size confound by re-engineering the target to a per-capita rate and applying VIF and LassoCV feature selection.",
      "Evaluated GLM and Random Forest models using cross-validation to prevent data leakage, identifying minimum temperature and rainfall as primary incidence drivers.",
      "Designed an interactive SAP Analytics Cloud (SAC) story incorporating geospatial maps, climate early-warning indicators, and predictive risk matrices to visualize regional incidence drivers for policy stakeholders."
    ],
  },
  {
    id: "shoplifting-thesis",
    role: "Shoplifting Recognition through Long-Range Temporal Modeling",
    org: "Undergraduate Thesis",
    link:"https://www.canva.com/design/DAGkzuGUvlQ/-lhxW0478p_Tp-UmMe63tw/edit",
    date: "Feb 2025 – Apr 2026",
    image: portShoplifting,
    categories: ["Data Science", "Software Development", "Project Management"],
    points: [
      "Designed a hybrid Encoder-Decoder TCN (ED-TCN) with a custom Spatial-Temporal Pooling attention mechanism, lifting F1-score to 53.12% (vs. 18.05% for a short-range baseline) on a held-out UCF-Crime test set.",
      "Built a YOLOv8 + HRNet pipeline to extract pose sequences across 50 surveillance videos, applying Stratified K-Fold CV and weighted loss to resolve severe class imbalance.",
      "Validated long-range modeling against short-range baselines using statistical significance testing across temporal windows; led a 4-person team in PyTorch/TensorFlow."
    ],
  },
  {
    id: "alzheimers-prediction",
    role: "Alzheimer’s Disease Prediction",
    org: "The Curiosity Cup: A Global SAS Competition 2025",
    link: "https://canva.link/4dbadi3ym2hz26b",
    date: "Jan 2025 – Apr 2025",
    image: portSas,
    categories: ["Data Science", "Data Analysis"],
    points: [
      "Built and tuned Gradient Boosting Classifiers using GridSearchCV across 3 clinical datasets (2,700+ records), achieving 96% accuracy in Alzheimer's diagnosis prediction.",
      "Ran correlation and feature-importance analysis to surface key clinical predictors (MMSE, CDR, functional assessment); presented findings to a non-technical panel, winning Data Preparation at SAS Curiosity Cup 2025."
    ],
  },
  {
    id: "amdocs",
    role: "Health Environment Dashboard",
    org: "Amdocs",
    link:"https://canva.link/ohe48u2rno06nib",
    date: "Jan 2026 – Apr 2026",
    image: portAmdocs,
    categories: ["Software Development", "Data Analysis"],
    points: [
      "Engineered an internal Bash-based Health Environment Dashboard for Korea Telecom (KT) infrastructure, reducing system troubleshooting latency from 15 minutes to under 30 seconds.",
      "Coordinated cross-functional requirements gathering to scope system health diagnostics and operational metrics."
    ],
  },
  {
    id: "rakso",
    role: "Event Booking Application",
    org: "Rakso CT",
    link:"https://canva.link/xtmle7sj0gnllv0",
    date: "Jan 2026 – Feb 2026",
    image: portRakso,
    categories: ["Project Management", "Software Development"],
    points: [
      "Directed a team of 7 developers and designers through the full development lifecycle of an event booking application, achieving a 100% on-time delivery rate.",
      "Facilitated daily standups, sprint planning, and retrospectives leveraging Agile methodologies in Jira to resolve development bottlenecks and increase velocity."
    ],
  },
  {
    id: "supply-chain-analytics",
    role: "Supply Chain Analytics (DataCo)",
    org: "DataCo Supply Chain",
    link: "https://dbc-c672d104-4132.cloud.databricks.com/dashboardsv3/01f1b11217651ef092a7167bcce9c185/published/pages/1a2eae62?o=7474647654101349",
    date: "2026",
    image: portDatacoSupplychain,
    categories: ["Data Analysis", "Data Science"],
    points: [
      "Audited 180K+ Databricks orders using SQL/Python, reclassifying 4.4K mislabeled shipments to optimize delivery routes."
    ],
  },
  {
    id: "kairos",
    role: "Kairos Pomodoro App",
    org: "Personal Project",
    link: "https://kairostime.app/",
    date: "Present",
    image: appKairos,
    categories: ["Software Development"],
    points: [
      "Developed a native iOS app using Swift and React Native, acquiring 170+ active users."
    ],
  },
  {
    id: "tiktok-automation",
    role: "TikTok Content Automation",
    org: "Independent Project",
    date: "2026",
    image: portTiktok,
    categories: ["Software Development"],
    points: [
      "Built an n8n automation pipeline using the Gemini API to auto-generate 120+ video assets."
    ],
  },
];

export default function Home() {
  useScrollReveal();

  return (
    <Motion>
      <Helmet>
        <title>DataCy</title>
      </Helmet>
      <div className="site-wrapper" style={{ backgroundColor: "#ffffff", color: "#0f172a" }}>
        <Navbar />
        <main>
          <Hero />
          <Projects items={projectsData} />
          <Contact />
        </main>
        <SiteFooter />
      </div>
      <SpeedInsights />
    </Motion>
  );
}

/* ── Navbar ─────────────────────────────────── */
export function Navbar() {
  const navLinkStyle = {
    color: "#0f172a",
    textDecoration: "none",
    fontWeight: 500,
    fontSize: "1rem",
    transition: "opacity 0.2s ease",
  };

  const scrollToSection = (e, targetId) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header className="aa-nav" style={{ padding: "16px 24px", borderBottom: "1px solid #f1f5f9", position: "sticky", top: 0, backgroundColor: "#ffffff", zIndex: 100 }}>
      <div
        className="aa-nav__inner"
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Link
          to="/"
          className="aa-nav__brand"
          onClick={(e) => scrollToSection(e, "hero")}
          style={{
            fontSize: "1.25rem",
            fontWeight: 700,
            textDecoration: "none",
            color: "#0f172a",
          }}
        >
          DataCy
        </Link>
        <nav style={{ display: "flex", gap: "24px", alignItems: "center" }}>
          <a href="#hero" onClick={(e) => scrollToSection(e, "hero")} style={navLinkStyle}>
            About
          </a>
          <a href="#projects" onClick={(e) => scrollToSection(e, "projects")} style={navLinkStyle}>
            Projects
          </a>
          <a href="#contact" onClick={(e) => scrollToSection(e, "contact")} style={navLinkStyle}>
            Contact
          </a>
        </nav>
      </div>
    </header>
  );
}

/* ── Hero ───────────────────────────────────── */
function Hero() {
  return (
    <section className="aa-hero reveal-initial" id="hero" style={{ padding: "50px 24px 20px 24px" }}>
      <div
        className="aa-hero__inner"
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          gap: "48px",
        }}
      >
        <div className="aa-hero__text" style={{ flex: "1 1 500px", minWidth: "300px" }}>
          <h1
            className="aa-hero__title"
            style={{
              fontSize: "3.5rem",
              fontWeight: 800,
              lineHeight: 1.1,
              margin: "0 0 16px 0",
              color: "#0f172a",
              letterSpacing: "-0.02em",
            }}
          >
            Cyrus Canape
          </h1>

          <p
            className="aa-hero__subtitle"
            style={{
              fontSize: "1.25rem",
              lineHeight: 1.6,
              color: "#475569",
              margin: "0 0 32px 0",
              maxWidth: "540px",
            }}
          >
            I build software and leverage data-driven strategies to optimize workflows, forecast trends, and detect anomalies to solve business problems.
          </p>

          <a
            href={process.env.PUBLIC_URL + "/CANAPE_Resume.pdf"}
            download="Cyrus_Canape_Resume.pdf"
            className="aa-hero__btn"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "#0f172a",
              color: "#ffffff",
              fontWeight: 600,
              fontSize: "1rem",
              padding: "12px 24px",
              borderRadius: "8px",
              textDecoration: "none",
              border: "1px solid #0f172a",
              transition: "all 0.2s ease-in-out",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#ffffff";
              e.currentTarget.style.color = "#0f172a";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#0f172a";
              e.currentTarget.style.color = "#ffffff";
            }}
          >
            Download CV
          </a>
        </div>

        <div
          className="aa-hero__image-container"
          style={{
            flex: "1 1 360px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src={datacyImg}
            alt="Cyrus Benjamin C. Canape"
            style={{
              width: "100%",
              maxWidth: "360px",
              height: "auto",
              borderRadius: "16px",
              objectFit: "cover",
            }}
          />
        </div>
      </div>
    </section>
  );
}

/* ── Projects Section ────────────────────────── */
function Projects({ items }) {
  const [selectedFilters, setSelectedFilters] = useState(["All"]);
  const [selectedProject, setSelectedProject] = useState(null);

  const handleFilterClick = (cat) => {
    if (cat === "All") {
      setSelectedFilters(["All"]);
      return;
    }

    const isSelected = selectedFilters.includes(cat);
    const updatedFilters = selectedFilters.includes("All")
      ? [cat]
      : isSelected
      ? selectedFilters.filter((f) => f !== cat)
      : [...selectedFilters, cat];

    const specificCategories = CATEGORIES.filter((c) => c !== "All");

    if (
      updatedFilters.length === 0 ||
      updatedFilters.length === specificCategories.length
    ) {
      setSelectedFilters(["All"]);
    } else {
      setSelectedFilters(updatedFilters);
    }
  };

  const filteredItems = items.filter((item) => {
    if (selectedFilters.includes("All")) return true;
    return item.categories.some((cat) => selectedFilters.includes(cat));
  });

  return (
    <section className="aa-projects reveal-on-scroll" id="projects" style={{ padding: "20px 0 64px 0" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 24px" }}>
        <h2 style={{ fontSize: "2rem", margin: "0 0 20px 0", fontWeight: "700", color: "#0f172a" }}>
          Projects
        </h2>

        {/* Filter Buttons */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "32px" }}>
          {CATEGORIES.map((cat) => {
            const isActive = selectedFilters.includes(cat);
            return (
              <button
                key={cat}
                onClick={() => handleFilterClick(cat)}
                style={{
                  padding: "8px 18px",
                  borderRadius: "20px",
                  border: "1px solid #0f172a",
                  backgroundColor: isActive ? "#0f172a" : "#ffffff",
                  color: isActive ? "#ffffff" : "#0f172a",
                  fontSize: "0.88rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.backgroundColor = "#f1f5f9";
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.backgroundColor = "#ffffff";
                }}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* Grid Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "24px",
          }}
        >
          {filteredItems.map((item) => (
            <div
              key={item.id}
              onClick={() => setSelectedProject(item)}
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                overflow: "hidden",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                flexDirection: "column",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#0f172a";
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 12px 20px -5px rgba(0, 0, 0, 0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e2e8f0";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {/* Image Container */}
              <div
                style={{
                  height: "190px",
                  backgroundColor: "#f8fafc",
                  borderBottom: "1px solid #f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.role}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#64748b",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      backgroundColor: "#f1f5f9",
                      padding: "16px",
                      textAlign: "center",
                    }}
                  >
                    {item.org}
                  </div>
                )}
              </div>

              {/* Title Section */}
              <div style={{ padding: "20px" }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "1.15rem",
                    fontWeight: 700,
                    color: "#0f172a",
                    lineHeight: 1.4,
                  }}
                >
                  {item.role}
                </h3>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal Portal */}
      {selectedProject && (
        <ProjectModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </section>
  );
}

/* ── Contact Section ─────────────────────────── */
function Contact() {
  const buttonStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "12px 24px",
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "0.95rem",
    textDecoration: "none",
    transition: "all 0.2s ease-in-out",
    minWidth: "140px",
  };

  return (
    <section
      className="aa-contact reveal-on-scroll"
      id="contact"
      style={{ padding: "48px 24px 64px 24px", backgroundColor: "transparent" }}
    >
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          textAlign: "center",
          padding: "56px 24px 24px 24px",
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <h2 style={{ fontSize: "2.25rem", fontWeight: 800, margin: "0 0 12px 0", color: "#0f172a" }}>
          Send a message!
        </h2>
        <p style={{ fontSize: "1.1rem", color: "#64748b", margin: "0 0 32px 0", maxWidth: "600px" }}>
          Feel free to reach out for collaborations, opportunities, or just a friendly chat.
        </p>

        <div style={{ display: "flex", justifyContent: "center", gap: "16px", flexWrap: "wrap", marginBottom: "32px" }}>
          <a
            href="mailto:cyrusc.canape@gmail.com"
            style={{
              ...buttonStyle,
              backgroundColor: "#0f172a",
              color: "#ffffff",
              border: "1px solid #0f172a",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#ffffff";
              e.currentTarget.style.color = "#0f172a";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#0f172a";
              e.currentTarget.style.color = "#ffffff";
            }}
          >
            Email Me &#8599;
          </a>

          <a
            href="https://www.linkedin.com/in/cyruscc/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...buttonStyle,
              backgroundColor: "#ffffff",
              color: "#0f172a",
              border: "1px solid #e2e8f0",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#0f172a";
              e.currentTarget.style.backgroundColor = "#f8fafc";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e2e8f0";
              e.currentTarget.style.backgroundColor = "#ffffff";
            }}
          >
            LinkedIn &#8599;
          </a>
        </div>

        {/* Positioned dynamically below buttons for standard document flow */}
        <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: "16px" }}>
          <img
            src={datacyImg}
            alt="Cyrus Benjamin C. Canape"
            style={{
              width: "70px",
              height: "60px",
              borderRadius: "12px",
              objectFit: "cover",
              opacity: 0.9,
            }}
          />
        </div>
      </div>
    </section>
  );
}

/* ── Project Modal Component ────────────────────── */
function ProjectModal({ project, onClose }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "unset";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const modalContent = (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.65)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "24px",
        boxSizing: "border-box",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          maxWidth: "580px",
          width: "100%",
          padding: "32px",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.3)",
          position: "relative",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close Modal"
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            border: "none",
            background: "none",
            fontSize: "1.25rem",
            color: "#64748b",
            cursor: "pointer",
            lineHeight: 1,
            padding: "4px",
          }}
        >
          &#10005;
        </button>

        <span style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: "500" }}>
          {project.date}
        </span>

        <h2 style={{ fontSize: "1.6rem", fontWeight: 700, margin: "8px 0 4px 0", color: "#0f172a" }}>
          {project.role}
        </h2>

        <p style={{ margin: "0 0 16px 0", color: "#0f172a", fontWeight: "600" }}>
          {project.link ? (
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#0f172a", textDecoration: "underline" }}
            >
              {project.org} &#8599;
            </a>
          ) : (
            project.org
          )}
        </p>

        <hr style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "16px 0" }} />

        <ul style={{ margin: 0, paddingLeft: "20px", color: "#334155", lineHeight: 1.6, fontSize: "0.95rem" }}>
          {project.points.map((point, idx) => (
            <li key={idx} style={{ marginBottom: "10px" }}>
              {point}
            </li>
          ))}
        </ul>

        <div style={{ marginTop: "28px", textAlign: "right" }}>
          <button
            onClick={onClose}
            style={{
              backgroundColor: "#0f172a",
              color: "#ffffff",
              border: "none",
              padding: "10px 24px",
              borderRadius: "8px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

/* ── Footer ─────────────────────────────────── */
export function SiteFooter() {
  return (
    <footer className="aa-footer" id="footer" style={{ padding: "32px 24px", borderTop: "1px solid #f1f5f9" }}>
      <div className="aa-footer__inner" style={{ maxWidth: "1100px", margin: "0 auto", display: "flex", justifyContent: "space-between", color: "#64748b", fontSize: "0.9rem" }}>
        <span className="aa-footer__name" style={{ fontWeight: 600, color: "#0f172a" }}>DataCy</span>
        <p className="aa-footer__copy" style={{ margin: 0 }}>© {new Date().getFullYear()} Cyrus Canape</p>
      </div>
    </footer>
  );
}